import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, TileLayer, Popup, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../styles.css';

const PunchMap = () => {
    const [userData, setUserData] = useState(null);
    const [branch, setBranch] = useState(null);
    const currentUserId = localStorage.getItem('id');
    const [isPunchInEnabled, setIsPunchInEnabled] = useState(true);
    const [isPunchOutEnabled, setIsPunchOutEnabled] = useState(false);
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);
    const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [punchLoading, setPunchLoading] = useState(false);

    useEffect(() => {
        const screenWidth = window.innerWidth;
        if (screenWidth < 520) {
            setIsSidebarVisible(false);
        } else {
            setIsSidebarVisible(true);
        }
    }, []);

    const handleToggleClick = () => {
        setIsSidebarVisible(!isSidebarVisible);
    };

    useEffect(() => {
        if (currentUserId) {
            axiosInstance.get(`/user/${currentUserId}/`)
                .then((response) => {
                    setUserData(response.data);
                    return axiosInstance.get(`/user/${currentUserId}/office-branch/`);
                })
                .then((branchResponse) => {
                    setBranch(branchResponse.data);
                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching user data or office branch:', error);
                    setLoading(false);
                });

            axiosInstance.get(`/attendance/user/${currentUserId}/`)
                .then((response) => {
                    const attendanceData = response.data;
                    console.log('Attendance data:', attendanceData);
                    const todayStr = new Date().toDateString();
                    const todayAttendance = attendanceData.find((attendance) => {
                        return new Date(attendance.date).toDateString() === todayStr;
                    });
                    console.log('Today attendance:', todayAttendance);

                    if (todayAttendance) {
                        if (todayAttendance.punch_in_time && todayAttendance.punch_out_time) {
                            // Already punched in and out - completed
                            setIsPunchInEnabled(true);
                            setIsPunchOutEnabled(false);
                        } else if (todayAttendance.punch_in_time) {
                            // Only punched in, enable punch out
                            setIsPunchInEnabled(false);
                            setIsPunchOutEnabled(true);
                        } else {
                            // Record exists but no punches (rare), enable punch in
                            setIsPunchInEnabled(true);
                            setIsPunchOutEnabled(false);
                        }
                    } else {
                        // No attendance for today, enable punch in
                        setIsPunchInEnabled(true);
                        setIsPunchOutEnabled(false);
                    }
                })
                .catch((error) => console.error('Error fetching attendance:', error));
        }
    }, [currentUserId]);

    const handlePunchIn = async () => {
        setPunchLoading(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const currentLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };

                    try {
                        const url = userData?.is_free_punch
                            ? '/free-punch/'
                            : '/attendance/punch/';

                        const payload = {
                            punch_type: 'in',
                            latitude: currentLocation.lat,
                            longitude: currentLocation.lng,
                            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        };

                        await axiosInstance.post(url, payload);

                        setIsPunchInEnabled(false);
                        setIsPunchOutEnabled(true);
                        toast.success('Successfully punched in!');
                    } catch (error) {
                        console.error('Error punching in:', error);
                        toast.error(error.response?.data?.detail || 'Error punching in.');
                    } finally {
                        setPunchLoading(false);
                    }
                },
                (error) => {
                    console.error('Error getting current location:', error);
                    toast.error('Unable to retrieve your location for punch in.');
                    setPunchLoading(false);
                }
            );
        } else {
            toast.error('Geolocation is not supported by this browser.');
            setPunchLoading(false);
        }
    };

    const handlePunchOut = async () => {
        setPunchLoading(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const currentLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };

                    try {
                        const url = userData?.is_free_punch
                            ? '/free-punch/'
                            : '/attendance/punch/';

                        const payload = {
                            punch_type: 'out',
                            latitude: currentLocation.lat,
                            longitude: currentLocation.lng,
                            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        };

                        await axiosInstance.post(url, payload);

                        setIsPunchInEnabled(true);
                        setIsPunchOutEnabled(false);
                        toast.success('Successfully punched out!');
                    } catch (error) {
                        console.error('Error punching out:', error);
                        toast.error(error.response?.data?.detail || 'Error punching out.');
                    } finally {
                        setPunchLoading(false);
                    }
                },
                (error) => {
                    console.error('Error getting current location:', error);
                    toast.error('Unable to retrieve your location for punch out.');
                    setPunchLoading(false);
                }
            );
        } else {
            toast.error('Geolocation is not supported by this browser.');
            setPunchLoading(false);
        }
    };

    // const officeBranchIcon = L.icon({
    //     iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    //     iconSize: [25, 41],
    //     iconAnchor: [12, 41],
    //     popupAnchor: [1, -34],
    //     tooltipAnchor: [16, -28],
    // });

    const officeBranchIcon = L.divIcon({
        html: `<div style="font-size: 30px;">📍</div>`,
        className: '', // remove default styles
        iconSize: [30, 30],
        iconAnchor: [15, 30], // bottom center
    });

    const handleLogout = () => {
        localStorage.clear();
        window.location.href = '/';
    };

    const profilePhotoUrl = userData?.profile_photo?.startsWith('http://')
        ? userData?.profile_photo.replace('http://', 'https://')
        : userData?.profile_photo;

    const menuItems = [
        { icon: '🏠', label: 'Dashboard', path: '/user-dashboard' },
        { icon: '👤', label: 'My Profile', path: `/user-profile/${userData?.id}` },
        { icon: '📅', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
        { icon: '📋', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
        { icon: '✏️', label: 'Create Leave Request', path: `/leave-request/${userData?.id}` },
        { icon: '💰', label: 'Leave Balance', path: '/leave-balance' },
        { icon: '📊', label: 'Leave Transactions', path: '/leave-transaction/' },
        // { icon: '📅', label: 'Calendar', path: '/calendar' },
        { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/', active: false },
        { icon: '&#128179;', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
    ];

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB').replace(/\//g, '-');
    };

    return (
        <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
            <ToastContainer />
            <Header
                onToggleClick={handleToggleClick}
                isSidebarVisible={isSidebarVisible}
            />

            <Sidebar
                isSidebarVisible={isSidebarVisible}
                currentUser={userData}
                menuItems={menuItems}
                handleLogout={handleLogout}
            />

            <div className="dashboard-main-content">
                <div className="punch-map-container">
                    <div className="punch-map-header">
                        <h1>📍 Office Location</h1>
                        <p>View office location and mark your attendance</p>
                    </div>

                    {loading ? (
                        <div className="punch-map-loading">
                            <div className="loading-spinner"></div>
                            <p>Loading office location...</p>
                        </div>
                    ) : branch ? (
                        <div className="punch-map-card">
                            <div className="punch-map-wrapper">
                                {/* <MapContainer 
                                    center={[branch.latitude, branch.longitude]} 
                                    zoom={15} 
                                    style={{ height: '450px', width: '100%' }}
                                >
                                    <TileLayer
                                        url="https://www.google.cn/maps/vt?lyrs=m@189&gl=cn&x={x}&y={y}&z={z}"
                                        attribution='&copy; OpenStreetMap contributors'
                                    />
                                    <Marker position={[branch.latitude, branch.longitude]} icon={officeBranchIcon}>
                                        <Popup>
                                            <div style={{ textAlign: 'center' }}>
                                                <strong>{branch.name || 'Office Branch'}</strong><br />
                                                <span style={{ fontSize: '12px', color: '#666' }}>Your designated work location</span>
                                            </div>
                                        </Popup>
                                    </Marker>
                                </MapContainer> */}
                                <MapContainer 
                                        center={[branch.latitude, branch.longitude]} 
                                        zoom={15} 
                                        style={{ height: '450px', width: '100%' }}
                                    >
                                        <LayersControl position="topright">

                                            {/* Default: Normal Map */}
                                            {/* <LayersControl.BaseLayer checked name="Map View">
                                                <TileLayer
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                    attribution="&copy; OpenStreetMap contributors"
                                                />
                                            </LayersControl.BaseLayer> */}

                                            {/* Google Map */}
                                            <LayersControl.BaseLayer checked name="Google Streets">
                                                <TileLayer
                                                    url="https://www.google.cn/maps/vt?lyrs=m&x={x}&y={y}&z={z}"
                                                />
                                            </LayersControl.BaseLayer>

                                            {/* Satellite View */}
                                            <LayersControl.BaseLayer name="Satellite">
                                                <TileLayer
                                                    url="https://www.google.cn/maps/vt?lyrs=s&x={x}&y={y}&z={z}"
                                                />
                                            </LayersControl.BaseLayer>

                                            {/* Hybrid View (Satellite + Labels) */}
                                            <LayersControl.BaseLayer name="Hybrid">
                                                <TileLayer
                                                    url="https://www.google.cn/maps/vt?lyrs=y&x={x}&y={y}&z={z}"
                                                />
                                            </LayersControl.BaseLayer>

                                        </LayersControl>

                                        <Marker 
                                            position={[branch.latitude, branch.longitude]} 
                                            icon={officeBranchIcon}
                                        >
                                            <Popup>
                                                <div style={{ textAlign: 'center' }}>
                                                    <strong>{branch.name || 'Office Branch'}</strong><br />
                                                    <span style={{ fontSize: '12px', color: '#666' }}>
                                                        Your designated work location
                                                    </span>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    </MapContainer>
                            </div>

                            <div className="punch-actions-section">
                                <div className="punch-status-card">
                                    <div className="status-indicator">
                                        <span className={`status-dot ${isPunchInEnabled ? 'ready' : 'active'}`}></span>
                                        <span className="status-text">
                                            {isPunchOutEnabled ? 'Punched In' : isPunchInEnabled && !isPunchOutEnabled ? 'Ready to Punch' : 'Completed'}
                                        </span>
                                    </div>
                                    <div className="current-time">
                                        {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </div>
                                </div>

                                <div className="punch-buttons-row">
                                    <button
                                        className={`punch-btn punch-in-btn ${!isPunchInEnabled || punchLoading ? 'disabled' : ''}`}
                                        onClick={handlePunchIn}
                                        disabled={!isPunchInEnabled || punchLoading}
                                    >
                                        <div className="punch-btn-icon">
                                            {punchLoading && isPunchInEnabled ? (
                                                <div className="mini-spinner"></div>
                                            ) : (
                                                <span>🚪</span>
                                            )}
                                        </div>
                                        <span className="punch-btn-label">Punch In</span>
                                        <span className="punch-btn-time">{new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </button>

                                    <div className="punch-divider">
                                        <span>OR</span>
                                    </div>

                                    <button
                                        className={`punch-btn punch-out-btn ${!isPunchOutEnabled || punchLoading ? 'disabled' : ''}`}
                                        onClick={handlePunchOut}
                                        disabled={!isPunchOutEnabled || punchLoading}
                                    >
                                        <div className="punch-btn-icon">
                                            {punchLoading && isPunchOutEnabled ? (
                                                <div className="mini-spinner"></div>
                                            ) : (
                                                <span>🏃</span>
                                            )}
                                        </div>
                                        <span className="punch-btn-label">Punch Out</span>
                                        <span className="punch-btn-time">{new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                                    </button>
                                </div>

                                <div className="punch-instructions">
                                    <p>📌 Make sure you're within the office premises to mark attendance</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="punch-map-empty">
                            <div className="empty-icon">🏢</div>
                            <h3>Office Location Not Available</h3>
                            <p>Please contact your administrator to set up your office location.</p>
                        </div>
                    )}
                </div>

                {isIdCardPopupVisible && (
                    <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
                        <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
                            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>×</button>
                            <div className="id-card-header">
                                <img src="/Teamhalt.png" alt="Logo" className="id-card-logo" />
                                <span>TEAMHALT PRIVATE LIMITED</span>
                            </div>
                            <div className="id-card-body">
                                <div className="id-card-profile">
                                    <img src={profilePhotoUrl || '/profile.jpg'} alt="Profile" className="id-card-photo" />
                                    <div className="id-card-info">
                                        <h4>{userData?.first_name} {userData?.last_name}</h4>
                                        <p className="id-designation">{userData?.designation}</p>
                                        <div className="id-dob">
                                            <strong>DOB:</strong>
                                            <span>{userData?.date_of_birth ? formatDate(userData.date_of_birth) : "N/A"}</span>
                                        </div>
                                    </div>
                                </div>
                                <hr className="id-card-divider" />
                                <div className="id-card-details">
                                    {[
                                        { label: "Emp ID", value: userData?.employee_code },
                                        { label: "Department", value: userData?.department },
                                        { label: "ESIC No.", value: userData?.esic_no },
                                        { label: "UAN No.", value: userData?.uan_no },
                                        { label: "Gender", value: userData?.gender },
                                        { label: "Contact No.", value: userData?.contact_number },
                                    ].map((item, idx) => (
                                        <div key={idx} className="detail-row">
                                            <strong>{item.label}:</strong>
                                            <span>{item.value || "N/A"}</span>
                                        </div>
                                    ))}
                                </div>
                                {typeof userData?.work_location_name === "string" && userData.work_location_name.toLowerCase() !== "noida" && (
                                    <div className="id-card-duty">
                                        <strong>On Contractual Duty Of:</strong>
                                        <span>{userData?.duty_of || "N/A"}</span>
                                    </div>
                                )}
                                <div className="id-card-footer">https://teamhalt.com/</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PunchMap;

